"use client";

import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { extractWorkflowVariables } from "./extract-workflow-variables";
import { getCurrentWord, replaceWord } from "./variable-textarea-utils";
import type { Edge, Node } from "@xyflow/react";
interface NodeDefinition {
  type: string;
  resultSchema?: {
    properties?: Record<string, {
      description?: string;
      type?: string;
    }>;
  };
}
interface PriceInputProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: number | string | undefined;
  onChange: (value: number | string) => void;
  required?: boolean;
  currentNodeId?: string;
  allNodes?: Node[];
  allEdges?: Edge[];
  nodeDefinitions?: NodeDefinition[];
}
export default function PriceInput({
  label,
  description,
  headerRight,
  value,
  onChange,
  required = false,
  currentNodeId = "",
  allNodes = [],
  allEdges = [],
  nodeDefinitions = []
}: PriceInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPlacement, setDropdownPlacement] = useState<"above" | "below">("below");
  const [currentTrigger, setCurrentTrigger] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [displayValue, setDisplayValue] = useState("");

  // Extract available variables from workflow
  const availableVariables = useMemo(() => {
    if (!currentNodeId || allNodes.length === 0) {
      return [];
    }
    return extractWorkflowVariables(currentNodeId, allNodes, allEdges, nodeDefinitions);
  }, [currentNodeId, allNodes, allEdges, nodeDefinitions]);

  // Initialize display value from the stored value
  useEffect(() => {
    if (value === null || value === undefined || value === "") {
      setDisplayValue("");
      return;
    }

    // If value contains variables, show as-is
    if (String(value).includes("{{")) {
      setDisplayValue(String(value));
      return;
    }

    // Otherwise show the numeric value
    setDisplayValue(String(value));
  }, [value]);
  const updateCursorTracking = useCallback(() => {
    if (inputRef.current) {
      setCursorPosition(inputRef.current.selectionStart || 0);
    }
  }, []);
  const onTextValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);

    // If value contains variables, save as-is
    if (newValue.includes("{{")) {
      onChange(newValue);
    } else {
      // Try to parse as number
      const numericValue = parseFloat(newValue);
      if (!isNaN(numericValue) && newValue !== "") {
        onChange(numericValue);
      } else if (newValue === "") {
        onChange("");
      } else {
        // Invalid number - still update display but don't call onChange with invalid value
        onChange(newValue);
      }
    }

    // Track cursor position
    updateCursorTracking();

    // Check if we should show dropdown
    if (inputRef.current && containerRef.current) {
      const currentWord = getCurrentWord(inputRef.current as any);
      if (currentWord.startsWith("{{")) {
        // Check available space
        const containerRect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - containerRect.bottom;
        const spaceAbove = containerRect.top;

        // Show above if not enough space below
        const showAbove = spaceBelow < 220 && spaceAbove > 220;
        setDropdownPlacement(showAbove ? "above" : "below");
        setDropdownVisible(true);
        setCurrentTrigger(currentWord);
      } else {
        setDropdownVisible(false);
        setCurrentTrigger("");
      }
    }
  };
  const onVariableSelect = useCallback((variableId: string) => {
    if (inputRef.current) {
      replaceWord(inputRef.current as any, variableId);
      setDropdownVisible(false);
      setCurrentTrigger("");

      // Update both display and actual value (variables are saved as-is)
      const newValue_0 = inputRef.current.value;
      setDisplayValue(newValue_0);
      onChange(newValue_0);

      // Refocus input
      inputRef.current.focus();
    }
  }, [onChange]);
  const handleMouseDown = useCallback((e_0: Event) => {
    e_0.preventDefault();
    e_0.stopPropagation();
  }, []);
  const handleKeyDown = useCallback((e_1: Event) => {
    const keyEvent = e_1 as KeyboardEvent;

    // Handle backspace on variables
    if (keyEvent.key === "Backspace" && inputRef.current) {
      const {
        selectionStart,
        value: currentValue
      } = inputRef.current;

      // Check if cursor is at the end of a variable }}
      if (selectionStart && selectionStart >= 2 && currentValue.substring(selectionStart - 2, selectionStart) === "}}") {
        // Find the start of this variable
        const beforeCursor = currentValue.substring(0, selectionStart);
        const match = beforeCursor.match(/({{[^}]+}})$/);
        if (match) {
          keyEvent.preventDefault();
          // Delete the entire variable
          const newValue_1 = currentValue.substring(0, selectionStart - match[1].length) + currentValue.substring(selectionStart);
          onChange(newValue_1);

          // Move cursor to where variable was
          setTimeout(() => {
            if (inputRef.current) {
              const newPos = selectionStart - match[1].length;
              inputRef.current.setSelectionRange(newPos, newPos);
            }
          }, 0);
          return;
        }
      }
    }
    if (!dropdownVisible) return;
    if (["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(keyEvent.key)) {
      keyEvent.preventDefault();
      if (keyEvent.key === "Escape") {
        setDropdownVisible(false);
        setCurrentTrigger("");
      }
    }
  }, [dropdownVisible, onChange]);
  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setDropdownVisible(false);
      setCurrentTrigger("");
    }, 200);
  }, []);
  useEffect(() => {
    const input = inputRef.current;
    const dropdown = dropdownRef.current;
    if (input) {
      input.addEventListener("keydown", handleKeyDown);
      input.addEventListener("blur", handleBlur);
      input.addEventListener("click", updateCursorTracking);
      input.addEventListener("keyup", updateCursorTracking);
    }
    if (dropdown) {
      dropdown.addEventListener("mousedown", handleMouseDown);
    }
    return () => {
      if (input) {
        input.removeEventListener("keydown", handleKeyDown);
        input.removeEventListener("blur", handleBlur);
        input.removeEventListener("click", updateCursorTracking);
        input.removeEventListener("keyup", updateCursorTracking);
      }
      if (dropdown) {
        dropdown.removeEventListener("mousedown", handleMouseDown);
      }
    };
  }, [handleKeyDown, handleBlur, handleMouseDown, updateCursorTracking]);
  const searchQuery = currentTrigger.replace("{{", "").toLowerCase();
  const filteredVariables = availableVariables.filter(v => v.id.toLowerCase().includes(searchQuery) || v.label.toLowerCase().includes(searchQuery));

  // Validate if current value is a valid number or variable
  const hasVariable = displayValue.includes("{{");
  const isValidNumber = !isNaN(parseFloat(displayValue)) && displayValue !== "";
  const isValid = hasVariable || isValidNumber || displayValue === "";
  return <div className="space-y-2">
      {label && <div className="flex items-center justify-between gap-3">
          <label className="field-label flex items-center gap-2">
            <span>{label}</span>
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
          {headerRight}
        </div>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}

      <div ref={containerRef} className="relative">
        <div className={cn("bg-ds-surface-card shadow-ds-soft relative rounded-lg border focus-within:ring-2 focus-within:ring-blue-500/20", isValid ? "border-ds-neutral-300 dark:border-ds-neutral-700 focus-within:border-blue-500" : "border-red-300 focus-within:border-red-500 focus-within:ring-red-500/20 dark:border-red-700")}>
          {/* Highlighter layer for variable backgrounds - shows blue for variables */}
          <div className="pointer-events-none absolute left-0 top-0 z-0 whitespace-pre break-words px-3 py-2 font-mono text-sm leading-relaxed" aria-hidden="true">
            {(displayValue || "").split(/({{[^}]+}})/g).map((part, i, arr) => {
            if (part.startsWith("{{") && part.endsWith("}}")) {
              // Calculate the position of this part in the original string
              const partsBeforeThis = arr.slice(0, i);
              const positionStart = partsBeforeThis.join("").length;
              const positionEnd = positionStart + part.length;

              // Check if cursor is right after this variable
              const cursorAtEnd = cursorPosition === positionEnd;

              // Check if this is a valid variable
              const isValidVar = availableVariables.some(v_0 => v_0.id === part);
              return <span key={i} className={isValidVar ? cursorAtEnd ? "rounded-ds-xs bg-blue-100 p-0.5 text-blue-600 ring-2 ring-blue-400 dark:bg-blue-950/20 dark:text-blue-400 dark:ring-blue-600" : "rounded-ds-xs bg-blue-100 p-0.5 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:ring-blue-900/40" : "ring-dashed rounded-sm bg-transparent p-0.5 text-blue-500 ring-1 ring-blue-300 dark:text-blue-500 dark:ring-blue-800"}>
                    {part}
                  </span>;
            }
            return <span key={i} className="text-transparent">
                  {part}
                </span>;
          })}
          </div>

          <div className="relative flex items-center">
            <span className="text-ds-text-muted pointer-events-none absolute left-3 text-sm font-medium">$</span>
            <Input ref={inputRef} type="text" value={displayValue} onChange={onTextValueChange} className="text-ds-text-primary relative z-10 border-0 bg-transparent py-2 pl-8 pr-3 font-mono leading-relaxed" placeholder="Type price or {{" />
          </div>
        </div>

        {/* Validation message */}
        {!isValid && displayValue !== "" && <p className="micro-label mt-1 text-red-600 dark:text-red-400">Please enter a valid number or variable</p>}

        {/* Variable Suggestions Dropdown - Grouped by category */}
        {dropdownVisible && filteredVariables.length > 0 && <div ref={dropdownRef} className={`absolute left-0 z-50 ${dropdownPlacement === "above" ? "bottom-full mb-1" : "top-full mt-1"}`}>
            <Command className="border-ds-border-default bg-ds-surface-card max-h-[280px] w-[360px] overflow-auto rounded-lg border shadow-xl">
              <CommandList>
                {Object.entries(filteredVariables.reduce((acc, v_1) => {
              if (!acc[v_1.category]) acc[v_1.category] = [];
              acc[v_1.category].push(v_1);
              return acc;
            }, {} as Record<string, typeof filteredVariables>)).map(([category, vars]) => <div key={category} className="border-ds-neutral-100 dark:border-ds-neutral-700/50 border-b last:border-0">
                    <div className="text-ds-text-secondary dark:bg-ds-neutral-900 sticky top-0 z-10 bg-zinc-50 px-3 py-2 text-xs font-semibold">
                      {category}
                    </div>
                    <CommandGroup>
                      {vars.map(variable => <CommandItem key={variable.id} value={variable.id} onSelect={() => onVariableSelect(variable.id)} className="cursor-pointer px-3 py-2">
                          <div className="flex flex-col gap-1">
                            <span className="rounded-ds-xs inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-50 to-indigo-100 px-2 py-1 font-mono text-sm text-blue-700 dark:from-blue-950/30 dark:to-indigo-950/30 dark:text-blue-400">
                              {variable.id}
                            </span>
                            <span className="label-xs">{variable.label}</span>
                          </div>
                        </CommandItem>)}
                    </CommandGroup>
                  </div>)}
              </CommandList>
            </Command>
          </div>}
      </div>
    </div>;
}